import { ethers } from 'ethers';
import logger from './logger';

// TimelockController schedule function ABI
const TIMELOCK_SCHEDULE_ABI = [
    {
        "inputs": [
            {
                "internalType": "address",
                "name": "target",
                "type": "address"
            },
            {
                "internalType": "uint256",
                "name": "value",
                "type": "uint256"
            },
            {
                "internalType": "bytes",
                "name": "data",
                "type": "bytes"
            },
            {
                "internalType": "bytes32",
                "name": "predecessor",
                "type": "bytes32"
            },
            {
                "internalType": "bytes32",
                "name": "salt",
                "type": "bytes32"
            },
            {
                "internalType": "uint256",
                "name": "delay",
                "type": "uint256"
            }
        ],
        "name": "schedule",
        "outputs": [],
        "stateMutability": "nonpayable",
        "type": "function"
    }
];

// TimelockController execute function ABI
const TIMELOCK_EXECUTE_ABI = [
    {
        "inputs": [
            {
                "internalType": "address",
                "name": "target",
                "type": "address"
            },
            {
                "internalType": "uint256",
                "name": "value",
                "type": "uint256"
            },
            {
                "internalType": "bytes",
                "name": "data",
                "type": "bytes"
            },
            {
                "internalType": "bytes32",
                "name": "predecessor",
                "type": "bytes32"
            },
            {
                "internalType": "bytes32",
                "name": "salt",
                "type": "bytes32"
            }
        ],
        "name": "execute",
        "outputs": [],
        "stateMutability": "payable",
        "type": "function"
    }
];

export interface DecodedScheduleParams {
    target: string;
    value: string;
    data: string;
    predecessor: string;
    salt: string;
    delay: string;
}

export interface DecodedExecuteParams {
    target: string;
    value: string;
    data: string;
    predecessor: string;
    salt: string;
}

/**
 * Decode timelock schedule function parameters from hex data
 */
export async function decodeScheduleFunctionParams(hexData: string): Promise<DecodedScheduleParams | null> {
    try {
        logger.log('🔍 Decoding schedule function parameters from hex data:', hexData.substring(0, 20) + '...');

        // Ensure hex data starts with 0x
        const cleanHexData = hexData.startsWith('0x') ? hexData : '0x' + hexData;

        // Create interface for decoding
        const iface = new ethers.utils.Interface(TIMELOCK_SCHEDULE_ABI);

        // Decode the function data
        const decoded = iface.decodeFunctionData('schedule', cleanHexData);

        const params: DecodedScheduleParams = {
            target: decoded.target,
            value: decoded.value.toString(),
            data: decoded.data,
            predecessor: decoded.predecessor,
            salt: decoded.salt,
            delay: decoded.delay.toString()
        };

        logger.log('✅ Successfully decoded schedule parameters:', {
            target: params.target,
            value: params.value,
            dataLength: params.data.length,
            predecessor: params.predecessor.substring(0, 20) + '...',
            salt: params.salt.substring(0, 20) + '...',
            delay: params.delay
        });

        return params;

    } catch (error) {
        logger.error('❌ Failed to decode schedule function parameters:', error);
        return null;
    }
}

/**
 * Decode timelock execute function parameters from hex data
 */
export async function decodeExecuteFunctionParams(hexData: string): Promise<DecodedExecuteParams | null> {
    try {
        logger.log('🔍 Decoding execute function parameters from hex data:', hexData.substring(0, 20) + '...');

        // Ensure hex data starts with 0x
        const cleanHexData = hexData.startsWith('0x') ? hexData : '0x' + hexData;

        // Create interface for decoding
        const iface = new ethers.utils.Interface(TIMELOCK_EXECUTE_ABI);

        // Decode the function data
        const decoded = iface.decodeFunctionData('execute', cleanHexData);

        const params: DecodedExecuteParams = {
            target: decoded.target,
            value: decoded.value.toString(),
            data: decoded.data,
            predecessor: decoded.predecessor,
            salt: decoded.salt
        };

        logger.log('✅ Successfully decoded execute parameters:', {
            target: params.target,
            value: params.value,
            dataLength: params.data.length,
            predecessor: params.predecessor.substring(0, 20) + '...',
            salt: params.salt.substring(0, 20) + '...'
        });

        return params;

    } catch (error) {
        logger.error('❌ Failed to decode execute function parameters:', error);
        return null;
    }
}

/**
 * Extract salt from schedule function hex data
 */
export async function extractSaltFromScheduleFunction(hexData: string): Promise<string | null> {
    try {
        const decoded = await decodeScheduleFunctionParams(hexData);
        if (decoded && decoded.salt) {
            logger.log('✅ Extracted salt from schedule function:', decoded.salt);
            return decoded.salt;
        }
        return null;
    } catch (error) {
        logger.error('❌ Failed to extract salt from schedule function:', error);
        return null;
    }
}

/**
 * Extract salt from execute function hex data
 */
export async function extractSaltFromExecuteFunction(hexData: string): Promise<string | null> {
    try {
        const decoded = await decodeExecuteFunctionParams(hexData);
        if (decoded && decoded.salt) {
            logger.log('✅ Extracted salt from execute function:', decoded.salt);
            return decoded.salt;
        }
        return null;
    } catch (error) {
        logger.error('❌ Failed to extract salt from execute function:', error);
        return null;
    }
}

/**
 * Validate that hex data is a timelock schedule function call
 */
export function isScheduleFunctionCall(hexData: string): boolean {
    try {
        const cleanHexData = hexData.startsWith('0x') ? hexData : '0x' + hexData;

        // Check if it starts with the schedule function selector
        const scheduleSelector = ethers.utils.id('schedule(address,uint256,bytes,bytes32,bytes32,uint256)').substring(0, 10);

        return cleanHexData.toLowerCase().startsWith(scheduleSelector.toLowerCase());
    } catch (error) {
        return false;
    }
}

/**
 * Validate that hex data is a timelock execute function call
 */
export function isExecuteFunctionCall(hexData: string): boolean {
    try {
        const cleanHexData = hexData.startsWith('0x') ? hexData : '0x' + hexData;

        // Check if it starts with the execute function selector
        const executeSelector = ethers.utils.id('execute(address,uint256,bytes,bytes32,bytes32)').substring(0, 10);

        return cleanHexData.toLowerCase().startsWith(executeSelector.toLowerCase());
    } catch (error) {
        return false;
    }
}

/**
 * Compute the operation ID for a timelock operation
 * This matches the Solidity implementation: keccak256(abi.encode(target, value, data, predecessor, salt))
 */
export function computeOperationId(
    target: string,
    value: string,
    data: string,
    predecessor: string,
    salt: string
): string {
    try {
        logger.log('🔍 Computing operation ID with parameters:', {
            target,
            value,
            data: data.substring(0, 20) + '...',
            predecessor,
            salt: salt.substring(0, 20) + '...'
        });

        // Encode the parameters using ABI encoding (same as Solidity abi.encode)
        const encoded = ethers.utils.defaultAbiCoder.encode(
            ['address', 'uint256', 'bytes', 'bytes32', 'bytes32'],
            [target, value, data, predecessor, salt]
        );

        // Hash the encoded data
        const operationId = ethers.utils.keccak256(encoded);

        logger.log('✅ Computed operation ID:', operationId);
        return operationId;

    } catch (error) {
        logger.error('❌ Error computing operation ID:', error);
        throw new Error(`Failed to compute operation ID: ${error instanceof Error ? error.message : String(error)}`);
    }
}

export default {
    decodeScheduleFunctionParams,
    decodeExecuteFunctionParams,
    extractSaltFromScheduleFunction,
    extractSaltFromExecuteFunction,
    isScheduleFunctionCall,
    isExecuteFunctionCall,
    computeOperationId
};
